import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { FadeUp, RevealLinesOnView } from "../Reveal";
import { CATEGORIES, FRAMES, FRAME_TABS, IMG } from "../../lib/site";

const EASE = [0.16, 1, 0.3, 1];

function BrandStatement() {
  const words = "Every photograph has a story. We turn those stories into beautiful prints, frames, gifts and wall art.".split(" ");
  return (
    <section className="bg-white py-24 md:py-36" data-testid="brand-statement">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-3">
            <RevealLinesOnView
              lines={["MEMORIES", "WORTH", "FRAMING."]}
              className="font-display text-5xl font-black leading-[0.86] tracking-tight text-pp-ink md:text-6xl [&>span:last-child_span]:text-pp-red"
            />
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <p className="flex flex-wrap font-display text-2xl font-medium leading-[1.3] tracking-tight text-pp-ink md:text-4xl">
              {words.map((w, i) => (
                <motion.span
                  key={i}
                  className="mr-[0.28em]"
                  initial={{ opacity: 0.12 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                >
                  {w}
                </motion.span>
              ))}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatWeCreate() {
  return (
    <section className="bg-white pb-8" data-testid="what-we-create">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-12 border-t border-neutral-200 pt-8">
          <p className="overline text-pp-red">What We Create</p>
          <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-pp-ink md:text-5xl">
            Choose how you want to keep your memories.
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {CATEGORIES.map((c, i) => (
            <FadeUp key={c.no} delay={i * 0.1}>
              <a href={`#${c.id}`} data-testid={`category-${c.id}`} data-cursor="view" className="group relative block h-[440px] overflow-hidden">
                <img src={c.image} alt={c.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-pp-black/85 via-pp-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-8">
                  <span className="font-display text-4xl font-black text-pp-red">{c.no}</span>
                  <h3 className="mt-2 font-display text-3xl font-bold text-white">{c.title}</h3>
                  <p className="mt-2 max-w-xs text-sm text-white/70">{c.desc}</p>
                </div>
              </a>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function FrameGrid() {
  return (
    <section id="frames" data-testid="frames-section" className="scroll-mt-24 bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-14 grid grid-cols-1 gap-6 md:grid-cols-12 md:items-end">
          <div className="md:col-span-8">
            <p className="overline mb-5 text-pp-red">Photo Frames</p>
            <RevealLinesOnView
              lines={["FIND YOUR", "PERFECT FRAME."]}
              className="font-display text-5xl font-black leading-[0.9] tracking-tight text-pp-ink md:text-7xl"
            />
          </div>
          <p className="text-neutral-600 md:col-span-4">Different memories deserve different frames — from warm wood to modern black, white, metallic and glass.</p>
        </div>

        <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-5">
          {FRAMES.map((f, i) => (
            <FadeUp key={f.no} delay={(i % 5) * 0.06}>
              <div data-testid={`frame-card-${f.no}`} data-cursor="view" className="group cursor-pointer">
                <div className="relative overflow-hidden border border-neutral-200 bg-neutral-50 transition-all duration-500 group-hover:-translate-y-1.5 group-hover:border-pp-red group-hover:shadow-[0_25px_50px_-20px_rgba(0,0,0,0.3)]">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={f.image} alt={f.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-bold text-pp-ink transition-colors group-hover:text-pp-red">{f.name}</h3>
                    <ArrowRight size={16} className="text-pp-red transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                  <p className="mt-1 text-xs text-neutral-500">{f.desc}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function FrameSelector() {
  const [active, setActive] = useState(0);
  const tab = FRAME_TABS[active];
  return (
    <section data-testid="frame-selector" className="bg-neutral-50 py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:items-center">
          <div className="md:col-span-5">
            <p className="overline mb-5 text-pp-red">Choose Your Frame</p>
            <h2 className="font-display text-4xl font-black leading-[0.95] tracking-tight text-pp-ink md:text-6xl">
              One memory,<br />endless frames.
            </h2>
            <div className="mt-8 flex flex-wrap gap-2">
              {FRAME_TABS.map((t, i) => (
                <button
                  key={t.key}
                  onClick={() => setActive(i)}
                  data-testid={`frame-tab-${t.key.toLowerCase()}`}
                  className={`pp-btn rounded-full border px-5 py-2.5 text-sm ${
                    active === i ? "border-pp-red bg-pp-red text-white" : "border-neutral-300 text-neutral-600 hover:border-pp-red"
                  }`}
                >
                  {t.key}
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.p
                key={active}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="mt-8 max-w-sm text-neutral-600"
              >
                {tab.note}
              </motion.p>
            </AnimatePresence>
            <button data-testid="frame-selector-cta" className="pp-btn mt-8 rounded-full bg-pp-ink px-6 py-3 text-sm text-white hover:bg-pp-red">
              View This Frame <ArrowRight size={16} />
            </button>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <div className="relative mx-auto aspect-[4/5] w-full max-w-sm border-[14px] border-white bg-white shadow-[0_40px_80px_-30px_rgba(0,0,0,0.4)]">
              <div className="relative h-full w-full overflow-hidden bg-neutral-100">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={active}
                    src={tab.image}
                    alt={tab.key}
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
              </div>
              <span className="absolute -bottom-4 left-4 bg-pp-red px-3 py-1.5 overline text-white">{tab.key} Frame</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GalleryWall() {
  const spots = [
    { image: IMG.woodFrame, name: "Premium Wood Frame", use: "Perfect for family portraits and timeless memories.", cls: "left-[4%] top-[8%] h-40 w-32 md:h-56 md:w-44", r: -3 },
    { image: IMG.galleryAssorted, name: "Collage Wall Set", use: "Many memories arranged into one statement.", cls: "left-[36%] top-[2%] h-48 w-40 md:h-64 md:w-52", r: 2 },
    { image: IMG.blackFrame, name: "Modern Black Frame", use: "Minimal and premium for any wall.", cls: "right-[4%] top-[12%] h-40 w-32 md:h-52 md:w-40", r: 3 },
    { image: IMG.canvas, name: "Canvas Wall Art", use: "Statement pieces that anchor a room.", cls: "left-[14%] bottom-[6%] h-44 w-36 md:h-56 md:w-44", r: 2 },
    { image: IMG.interiorWhite, name: "White Gallery Frame", use: "Bright, clean edges for a modern look.", cls: "right-[14%] bottom-[4%] h-48 w-40 md:h-60 md:w-48", r: -2 },
  ];
  return (
    <section id="wallart" data-testid="gallery-wall" className="scroll-mt-24 bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-12 md:mb-16">
          <p className="overline mb-5 text-pp-red">Wall Art & Gallery Walls</p>
          <RevealLinesOnView
            lines={["CREATE YOUR OWN", "GALLERY WALL."]}
            className="font-display text-5xl font-black leading-[0.9] tracking-tight text-pp-ink md:text-7xl"
          />
        </div>
        <div className="relative h-[460px] overflow-hidden bg-neutral-100 md:h-[560px]">
          <img src={IMG.heroInterior} alt="Home interior" className="absolute inset-0 h-full w-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-white/40" />
          {spots.map((s, i) => (
            <motion.div
              key={i}
              className={`group absolute border-[6px] border-white bg-white shadow-[0_25px_50px_-20px_rgba(0,0,0,0.4)] ${s.cls}`}
              style={{ rotate: `${s.r}deg` }}
              initial={{ opacity: 0, y: 24, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.12 }}
              data-cursor="view"
            >
              <div className="h-full w-full overflow-hidden">
                <img src={s.image} alt={s.name} className="h-full w-full object-cover" />
              </div>
              <div className="absolute inset-0 flex flex-col justify-end bg-pp-black/70 p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <p className="text-xs font-bold text-white">{s.name}</p>
                <p className="mt-1 text-[10px] leading-snug text-white/70">{s.use}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Products() {
  return (
    <>
      <BrandStatement />
      <WhatWeCreate />
      <FrameGrid />
      <FrameSelector />
      <GalleryWall />
    </>
  );
}
